import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Calendar, User, ArrowRight, Twitter, Instagram, Linkedin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = "https://effendialogue-2.onrender.com";
const API = `${BACKEND_URL}/api`;

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: "All Posts" },
    { id: "Human VS Computer", name: "Human VS Computer" },
    { id: "World Around", name: "World Around" },
    { id: "Being Human", name: "Being Human" }
  ];

  useEffect(() => {
    fetchPosts();
  }, [selectedCategory]);

  useEffect(() => {
  document.title = "Effendialogue | Articles";
}, []);

const fetchPosts = async () => {
  try {
    setLoading(true);
    let url = `${API}/posts`;

    if (selectedCategory !== "all") {
      url += `?category=${selectedCategory}`;
    }

    const response = await axios.get(url);
    setPosts(response.data);
  } catch (error) {
    console.error("Error fetching posts:", error);
    toast.error("Failed to fetch blog posts");
  } finally {
    setLoading(false);
  }
};

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchPosts();
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/posts/search?q=${encodeURIComponent(searchQuery)}`);
      setPosts(response.data);
      setSelectedCategory("all"); // Reset category when searching
    } catch (error) {
      console.error("Error searching posts:", error);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  return (
    <div className="app-container min-h-screen">
      <div className="content-wrapper" style={{ paddingTop: '6rem', paddingBottom: '3rem' }}>
        {/* Page Header */}
        <div className="text-center mb-16 fade-in">
          <h1 className="text-5xl font-bold gradient-text mb-4">
            Articles Archive
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
            Explore our complete collection of articles on technology, culture, and ideas.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="search-container mb-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                data-testid="blog-search-input"
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input pl-10 bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-yellow-500"
              />
              <Button 
                data-testid="blog-search-button"
                type="submit" 
                className="search-button bg-yellow-500 hover:bg-yellow-600 text-black px-6"
              >
                Search
              </Button>
            </div>
          </form>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            {categories.map((category) => (
              <Button
                key={category.id}
                data-testid={`category-${category.id}`}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? "default" : "outline"}
                className={`${
                  selectedCategory === category.id
                    ? "bg-yellow-500 text-black hover:bg-yellow-600"
                    : "border-gray-600 text-gray-300 hover:border-black hover:text-black"
                }`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-700 rounded-lg h-64"></div>
              </div>
            ))}
          </div>
        )}

        {/* Posts Grid */}
        {!loading && posts.length === 0 && (
          <div className="text-center py-20">
            <h2 className="text-2xl text-gray-400 mb-4">
              {searchQuery ? "No search results" : `No ${selectedCategory} posts`}
            </h2>
            <p className="text-gray-500 mb-6">
              {searchQuery 
                ? "Try a different search term or browse by category" 
                : `No posts found in the ${selectedCategory} category yet.`
              }
            </p>
            {searchQuery && (
              <Button
                onClick={() => {
                  setSearchQuery("");
                  fetchPosts();
                }}
                className="bg-yellow-500 hover:bg-yellow-600 text-black px-6"
              >
                Clear Search
              </Button>
            )}
          </div>
        )}

        {!loading && posts.length > 0 && (
          <>

            {/* Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <Card
                  key={post.id}
                  data-testid={`blog-post-card-${post.id}`}
                  className="post-card slide-in"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {post.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                  )}
                  <CardHeader className="pb-3">
                    <div className="post-meta flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4" />
                      <span>{formatRelativeDate(post.created_at)}</span>
                      <User className="h-4 w-4 ml-2" />
                      <span>{post.author}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="category-badge text-xs">
                        {post.category}
                      </Badge>
                    </div>
                    <h3 className="post-title text-xl font-semibold line-clamp-2 transition-colors">
                      {post.title}
                    </h3>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="post-excerpt line-clamp-3 mb-4">
                      {post.excerpt || post.content.substring(0, 150) + "..."}
                    </p>
                    <Link 
                      to={`/post/${post.id}`}
                      data-testid={`blog-read-more-${post.id}`}
                    >
                        <Button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 font-semibold">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-grid">
            <div>
              <h4 className="text-xl font-bold gradient-text mb-4">About Effendialogue</h4>
              <p className="text-gray-400 leading-relaxed">
                This is the place where I share you my thoughts that will hopefully make us appreciate every little thing behind every step that we been through.
              </p>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white mb-3">Quick Links</h5>
              <div className="footer-links">
                <Link to="/" className="footer-link">Home</Link>
                <Link to="/articles" className="footer-link">Articles</Link>
              </div>
            </div>
            <div>
              <h5 className="text-lg font-semibold text-white mb-3">Follow Me</h5>
              <div className="social-icons">
                <a href="https://x.com/fadlaneffendii" target="_blank" className="social-icon">
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="https://instagram.com/fadlaneffendi_" target="_blank" className="social-icon">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href="https://www.linkedin.com/in/fadlan-raya-effendi-ba8585275/" target="_blank" className="social-icon">
                  <Linkedin className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4 text-center">
            <p className="text-gray-500">© 2025 Effendialogue. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;